import React, { createContext, useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { TOKEN_ADDRESS, MINING_ADDRESS, CHAIN_ID, RPC_URL } from '../config';
import TokenABI from '../abis/TokenContract.json';
import MiningABI from '../abis/MiningContract.json';

const WalletContext = createContext(null);

export const useWallet = () => {
    const context = useContext(WalletContext);
    if (!context) {
        throw new Error('useWallet must be used within WalletProvider');
    }
    return context;
};

export const WalletProvider = ({ children }) => {
    const [account, setAccount] = useState(null);
    const [provider, setProvider] = useState(null);
    const [signer, setSigner] = useState(null);
    const [tokenContract, setTokenContract] = useState(null);
    const [miningContract, setMiningContract] = useState(null);
    const [chainId, setChainId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (window.ethereum) {
            window.ethereum.on('accountsChanged', handleAccountsChanged);
            window.ethereum.on('chainChanged', handleChainChanged);
            checkConnection();
        }
        return () => {
            if (window.ethereum) {
                window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
                window.ethereum.removeListener('chainChanged', handleChainChanged);
            }
        };
    }, []);

    const checkConnection = async () => {
        try {
            const provider = new ethers.BrowserProvider(window.ethereum);
            const accounts = await provider.listAccounts();
            if (accounts.length > 0) {
                const signer = await provider.getSigner();
                const address = await signer.getAddress();
                const network = await provider.getNetwork();
                setAccount(address);
                setProvider(provider);
                setSigner(signer);
                setChainId(Number(network.chainId));
                initContracts(signer);
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const initContracts = (signer) => {
        setTokenContract(new ethers.Contract(TOKEN_ADDRESS, TokenABI, signer));
        setMiningContract(new ethers.Contract(MINING_ADDRESS, MiningABI, signer));
    };

    const connectWallet = async () => {
        try {
            if (!window.ethereum) {
                toast.error('请安装 MetaMask');
                return;
            }
            const provider = new ethers.BrowserProvider(window.ethereum);
            await provider.send('eth_requestAccounts', []);
            const signer = await provider.getSigner();
            const address = await signer.getAddress();
            const network = await provider.getNetwork();
            setAccount(address);
            setProvider(provider);
            setSigner(signer);
            setChainId(Number(network.chainId));
            initContracts(signer);
            toast.success('钱包连接成功');
        } catch (error) {
            toast.error('连接失败: ' + error.message);
        }
    };

    const disconnectWallet = () => {
        setAccount(null);
        setSigner(null);
        setTokenContract(null);
        setMiningContract(null);
        setChainId(null);
    };

    const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
            disconnectWallet();
        } else {
            setAccount(accounts[0]);
        }
    };

    const handleChainChanged = (chainId) => {
        setChainId(Number(chainId));
        window.location.reload();
    };

    return (
        <WalletContext.Provider
            value={ {
                account,
                provider,
                signer,
                tokenContract,
                miningContract,
                chainId,
                connectWallet,
                disconnectWallet,
                loading,
            } }
        >
            { children }
        </WalletContext.Provider>
    );
};